import { Component, inject, OnInit } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { NgScrollbar } from 'ngx-scrollbar';
import { WithdrawalService } from 'src/infrastructure/adapters/services/withdrawal.service';
import { WithdrawalStatus } from '@core-interfaces/dtos/withdrawal';
import { SosService } from 'src/infrastructure/adapters/services/sos.service';
import { SosStatus } from '@core-interfaces/dtos/sos';
import { ChatAdminService } from '@infra-adapters/services/chat-admin.service';
import { AuthService } from '@infra-adapters/services/auth.service';
import { PermissionsService } from '@infra-adapters/services/permissions.service';
import type { INavData } from '@coreui/angular-pro';

import { IconDirective } from '@coreui/icons-angular';
import {
  ButtonCloseDirective,
  ContainerComponent,
  ShadowOnScrollDirective,
  SidebarBrandComponent,
  SidebarComponent,
  SidebarHeaderComponent,
  SidebarNavComponent,
  SidebarToggleDirective,
  SidebarTogglerDirective,
  ModalComponent,
  ModalHeaderComponent,
  ModalBodyComponent,
  ModalFooterComponent
} from '@coreui/angular-pro';

import {
  DefaultAsideComponent,
  DefaultBreadcrumbComponent,
  DefaultFooterComponent,
  DefaultHeaderComponent,
} from '.';
import { navItems, filterNavByPermissions } from './_nav';

function isOverflown(element: HTMLElement) {
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  );
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './default-layout.component.html',
  styleUrls: ['./default-layout.component.scss'],
  imports: [
    SidebarComponent,
    SidebarHeaderComponent,
    SidebarBrandComponent,

    //IconDirective,
    NgScrollbar,
    SidebarNavComponent,

    SidebarToggleDirective,
    //SidebarTogglerDirective,
    DefaultAsideComponent,
    DefaultHeaderComponent,
    ShadowOnScrollDirective,
    ContainerComponent,
    RouterOutlet,
    DefaultFooterComponent,
    ButtonCloseDirective,
    DefaultBreadcrumbComponent,
    ModalComponent,
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent
  ],
  standalone: true,
})
export class DefaultLayoutComponent implements OnInit {
  private router = inject(Router);
  private withdrawalService = inject(WithdrawalService);
  private sosService = inject(SosService);
  private chatAdminService = inject(ChatAdminService);
  private authService = inject(AuthService);
  private permissionsService = inject(PermissionsService);
  /** Menú ya filtrado por permisos; el badge de retiros se aplica sobre esta base. */
  private baseNav: INavData[] = navItems;
  public navItems = navItems;
  showLogoutModal = false;

  ngOnInit(): void {
    // Filtrar el menú según los permisos del usuario antes de renderizarlo.
    this.permissionsService.ensureLoaded().subscribe(() => {
      this.baseNav = filterNavByPermissions(navItems, (permission) =>
        this.permissionsService.hasAny(
          Array.isArray(permission) ? permission : [permission],
        ),
      );
      this.navItems = this.baseNav;
      this.loadPendingWithdrawalsBadge();
      this.loadInProgressSosBadge();
      this.loadPendingChatReportsBadge();
    });
    // Recargar cada badge cuando una acción cambie su cantidad pendiente.
    this.withdrawalService.pendingBadgeRefresh$.subscribe(() => {
      this.loadPendingWithdrawalsBadge();
    });
    this.sosService.pendingBadgeRefresh$.subscribe(() => {
      this.loadInProgressSosBadge();
    });
    this.chatAdminService.pendingBadgeRefresh$.subscribe(() => {
      this.loadPendingChatReportsBadge();
    });
  }

  /** Setea (o limpia) el badge "danger" de un item del menú según un conteo, preservando los demás. */
  private setMenuBadge(url: string, count: number): void {
    this.navItems = this.navItems.map((item) =>
      item.url === url
        ? { ...item, badge: count > 0 ? { color: 'danger', text: String(count) } : undefined }
        : item,
    );
  }

  /** Muestra en el menú "Retiros" un badge con la cantidad de retiros pendientes de aprobación. */
  private loadPendingWithdrawalsBadge(): void {
    this.withdrawalService
      .getWithdrawals({ first: 1, after: 0, statuses: [WithdrawalStatus.pending_approval] })
      .subscribe({
        next: (data) => {
          this.setMenuBadge('/retiros', data.total_count);
        },
        error: () => {
          // Silencioso: si falla, el menú simplemente no muestra el badge.
        },
      });
  }

  /** Muestra en el menú "Solicitudes SOS" un badge con la cantidad de SOS en curso. */
  private loadInProgressSosBadge(): void {
    this.sosService
      .getSosRequests({ first: 1, after: 0, status: SosStatus.in_progress })
      .subscribe({
        next: (data) => {
          this.setMenuBadge('/sos', data.total_count);
        },
        error: () => {
          // Silencioso: si falla, el menú simplemente no muestra el badge.
        },
      });
  }

  /** Muestra en el menú "Mensajes" un badge con la cantidad de chats con reporte pendiente. */
  private loadPendingChatReportsBadge(): void {
    this.chatAdminService
      .getConversations({ page: 1, limit: 1, report_filter: 'pending' })
      .subscribe({
        next: (data) => {
          this.setMenuBadge('/mensajes', data.total_count);
        },
        error: () => {
          // Silencioso: si falla, el menú simplemente no muestra el badge.
        },
      });
  }

  onScrollbarUpdate($event: any) {
  }

  onShowModal(show: boolean) {
    this.showLogoutModal = show;
  }

  closeLogoutModal() {
    this.showLogoutModal = false;
  }

  logout() {
    this.authService.cleanSession();
    this.permissionsService.clear();
    this.router.navigate(['/login']);
  }
}
